/**
 * `useProjectParties` — derives the parties relevant to a single
 * project by joining transactions (and later tasks/attendance) on the
 * shared `partyId`.
 *
 * Parties aren't project-scoped in Firestore. A party can appear on
 * many projects via the docs that reference it. This hook unions the
 * partyIds referenced by the project's transactions, then resolves
 * them against `useParties()` (org-wide).
 *
 * In a future iteration we'll also fold in attendance.labourId and
 * tasks.assignedTo. For this iteration we only mine transactions.
 */
import { useMemo } from 'react';

import type { Party } from '@/lib/parties';
import { useParties } from '@/lib/useParties';
import {
  isCountedInTotals,
  normaliseTxType,
  useProjectTransactions,
  type TransactionItem,
} from '@/lib/useProjectTransactions';

export type ProjectPartyBalance = {
  totalIn: number;
  totalOut: number;
  balance: number;
  txnCount: number;
};

export type ProjectPartyRow = {
  party: Party;
  balance: ProjectPartyBalance;
};

/**
 * A transaction that has `partyName` (free-text) but no `partyId`.
 * These are surfaced separately so the customer can link them to an
 * existing party or create a new party from the typed name. Without
 * this, free-text transactions (created on web before the picker
 * shipped) silently disappear from the Party tab.
 *
 * Grouped by lower-cased trimmed name so duplicate typings of the
 * same name ("raju" vs "Raju") collapse into one row.
 */
export type UnlinkedPartyRow = {
  /** Stable React key — `unlinked:<lower-name>`. */
  id: string;
  /** Best-capitalised form we saw in any matching transaction. */
  name: string;
  /** Lower-cased trimmed name used for grouping. Caller passes this
   *  back to `linkPartyOnTransactions` so the batched update can
   *  case-insensitively match every transaction to backfill. */
  nameKey: string;
  /** All matching project transactions. */
  transactions: TransactionItem[];
  balance: ProjectPartyBalance;
};

export type UseProjectPartiesResult = {
  parties: ProjectPartyRow[];
  /** Transactions with a typed `partyName` but no `partyId`,
   *  grouped by name. Empty once every transaction has a party
   *  picked. */
  unlinked: UnlinkedPartyRow[];
  loading: boolean;
  error: string | null;
  /** All transactions for this project — bubbled up so callers don't
   *  re-subscribe just to compute summary numbers. */
  transactions: TransactionItem[];
};

export function useProjectParties(
  orgId: string | null,
  projectId: string | undefined,
): UseProjectPartiesResult {
  const { parties: orgParties, loading: pLoading, error: pErr } = useParties();
  const { transactions, loading: txLoading, error: txErr } =
    useProjectTransactions(orgId, projectId);

  const result = useMemo(() => {
    // ── Linked rows (transactions WITH partyId, joined to party doc) ──
    const byId = new Map<string, ProjectPartyBalance>();
    // ── Unlinked rows (transactions WITHOUT partyId, grouped by name) ──
    const unlinkedAgg = new Map<
      string,
      {
        name: string;
        balance: ProjectPartyBalance;
        transactions: TransactionItem[];
      }
    >();

    for (const t of transactions) {
      const k = normaliseTxType(t.type);
      const counted = isCountedInTotals(t);

      if (t.partyId) {
        // Linked branch — needs both a valid type AND posted status to
        // contribute to balance.
        if (!counted || !k) continue;
        const e = byId.get(t.partyId) ?? {
          totalIn: 0,
          totalOut: 0,
          balance: 0,
          txnCount: 0,
        };
        if (k === 'in') e.totalIn += t.amount ?? 0;
        else e.totalOut += t.amount ?? 0;
        e.balance = e.totalIn - e.totalOut;
        e.txnCount += 1;
        byId.set(t.partyId, e);
        continue;
      }

      // Unlinked branch — only group if there's a typed name. Skip
      // transactions with neither partyId nor partyName (those have no
      // counter-party and don't belong in the Party tab at all).
      const name = (t.partyName ?? '').trim();
      if (!name) continue;
      const key = name.toLowerCase();
      const agg = unlinkedAgg.get(key) ?? {
        name,
        balance: { totalIn: 0, totalOut: 0, balance: 0, txnCount: 0 },
        transactions: [] as TransactionItem[],
      };
      // Prefer the longest / most-capitalised name we've seen.
      if (name.length > agg.name.length) agg.name = name;
      agg.transactions.push(t);
      if (counted && k) {
        if (k === 'in') agg.balance.totalIn += t.amount ?? 0;
        else agg.balance.totalOut += t.amount ?? 0;
        agg.balance.balance = agg.balance.totalIn - agg.balance.totalOut;
      }
      // Count ALL matching transactions in `txnCount` so the UI says
      // "3 txns" even when none are posted yet (matches the linked
      // counter — `byId.get()` counts everything that hits the loop).
      agg.balance.txnCount += 1;
      unlinkedAgg.set(key, agg);
    }

    const linked: ProjectPartyRow[] = [];
    for (const p of orgParties) {
      const bal = byId.get(p.id);
      if (!bal) continue;
      linked.push({ party: p, balance: bal });
    }
    linked.sort((a, b) => a.party.name.localeCompare(b.party.name));

    const unlinked: UnlinkedPartyRow[] = Array.from(unlinkedAgg.entries())
      .map(([nameKey, agg]) => ({
        id: `unlinked:${nameKey}`,
        name: agg.name,
        nameKey,
        transactions: agg.transactions,
        balance: agg.balance,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return { linked, unlinked };
  }, [orgParties, transactions]);

  return {
    parties: result.linked,
    unlinked: result.unlinked,
    loading: pLoading || txLoading,
    error: pErr || txErr,
    transactions,
  };
}
